/**
 * Hoodie/Profile Data Wrangler — API helpers.
 * Derived from hoodie/serializers.py and hoodie/permissions.py.
 */

import { formatApiError, normalizeListingsResponse } from "./listingsWranglerUtils";

export { formatApiError, normalizeListingsResponse };

export const HOODIE_PROFILE_SERIALIZER_FIELDS = [
  "id",
  "user",
  "zip_code",
  "recommendations",
  "gravatar_url",
];

export const USER_SERIALIZER_FIELDS = [
  "id",
  "username",
  "first_name",
  "last_name",
  "email",
  "last_login",
];

/**
 * @param {unknown} data
 */
export function normalizeHoodiesResponse(data) {
  if (data == null) {
    return {
      results: [],
      count: null,
      next: null,
      previous: null,
      isPaginated: false,
    };
  }
  if (Array.isArray(data)) {
    return {
      results: data,
      count: data.length,
      next: null,
      previous: null,
      isPaginated: false,
    };
  }
  if (typeof data === "object" && Array.isArray(data.results)) {
    return {
      results: data.results,
      count: typeof data.count === "number" ? data.count : null,
      next: data.next ?? null,
      previous: data.previous ?? null,
      isPaginated: true,
    };
  }
  return {
    results: [],
    count: null,
    next: null,
    previous: null,
    isPaginated: false,
  };
}

/**
 * @param {unknown} user
 */
export function formatUserLabel(user) {
  if (!user || typeof user !== "object") return "—";
  const u = user;
  const name = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
  if (name && u.email) return `${name} <${u.email}>`;
  if (u.email) return String(u.email);
  if (u.username) return String(u.username);
  if (u.id != null) return `user #${u.id}`;
  return "—";
}

/**
 * Extract listing ids from HoodieProfile.recommendations (various shapes).
 * @param {unknown} recommendations
 * @returns {number[]}
 */
export function extractRecommendationListingIds(recommendations) {
  if (!recommendations) return [];
  if (!Array.isArray(recommendations)) return [];
  const ids = [];
  for (const item of recommendations) {
    if (typeof item === "number") {
      ids.push(item);
    } else if (typeof item === "object" && item != null) {
      const id =
        item.listing__id ??
        item.listing_id ??
        item.listing ??
        item.id;
      if (id != null && Number.isFinite(Number(id))) {
        ids.push(Number(id));
      }
    }
  }
  return [...new Set(ids)];
}

/**
 * Client-side filter for hoodie rows (list endpoint rarely available).
 * @param {any[]} rows
 * @param {string} q
 */
export function filterHoodieRows(rows, q) {
  const needle = (q || "").trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter((row) => {
    const parts = [
      row?.id,
      row?.zip_code,
      row?.user?.email,
      row?.user?.username,
      row?.user?.first_name,
      row?.user?.last_name,
    ];
    return parts.some((p) =>
      String(p ?? "")
        .toLowerCase()
        .includes(needle)
    );
  });
}

/**
 * @param {unknown} value
 */
export function displayValue(value) {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

/**
 * Resolve legacy auth_user id from a hoodie profile API response.
 * @param {Record<string, unknown> | null | undefined} profile
 * @returns {number | null}
 */
export function resolveLegacyUserId(profile) {
  if (!profile) return null;
  const user = profile.user;
  if (user == null) return null;
  if (typeof user === "number" && Number.isFinite(user)) return user;
  if (typeof user === "object" && user.id != null) {
    const id = Number(user.id);
    return Number.isFinite(id) ? id : null;
  }
  return null;
}

/**
 * User-facing message for mlist-api relationship graph bridge errors.
 * @param {unknown} err
 * @param {{ configured: boolean }} options
 */
export function relationshipGraphBridgeMessage(err, { configured, valid }) {
  const status = err && typeof err === "object" ? err.status : undefined;
  if (!configured) {
    return {
      kind: "warn",
      title: "Relationship bridge not configured",
      detail: "Set REACT_APP_MLIST_API_BASE_URL to enable the relationship graph.",
    };
  }
  if (configured && valid === false) {
    return {
      kind: "warn",
      title: "Invalid MList API base URL",
      detail:
        "Fix REACT_APP_MLIST_API_BASE_URL (include a single http:// or https:// prefix).",
    };
  }
  if (status === 401 || status === 403) {
    return {
      kind: "warn",
      title: "Staff API auth required",
      detail:
        "This panel is served by mlist-api and requires a staff JWT (Bearer token).",
    };
  }
  if (status === 404) {
    return {
      kind: "warn",
      title: "Relationship bridge unavailable or user not found",
      detail:
        "The bridge may be disabled, not routed, or this legacy user id does not exist.",
    };
  }
  if (status === 503) {
    return {
      kind: "warn",
      title: "Relationship bridge enabled but legacy readonly DB is not configured",
      detail: "Set LEGACY_DATABASE_URL on mlist-api and restart the API.",
    };
  }
  return {
    kind: "error",
    title: "Relationship bridge error",
    detail: formatApiError(err),
  };
}
